import { useAuditEvents, useSystemStatus } from '../hooks/queries';
import { dateTime, humanize } from '../lib/format';
import { useLive } from '../store/live';
import { Empty, Panel } from './Panel';

function Status({ ok, label }: { ok: boolean | null; label: string }) {
  if (ok === null) return <span className="badge badge--neutral">{label}: not configured</span>;
  return (
    <span className={`badge ${ok ? 'badge--live' : 'badge--danger'}`}>
      {ok ? '✓' : '✕'} {label}
    </span>
  );
}

export function SystemHealthPanel() {
  const { data, isError, isLoading } = useSystemStatus();
  const connections = useLive((s) => s.connections);
  const gaps = useLive((s) => s.gapCount);
  const counters = data?.metrics.counters ?? {};
  return (
    <Panel title="System health" meta={data ? `v${data.version} · ${data.env}` : undefined}>
      {isLoading ? <p className="small muted">Loading system status…</p> : null}
      {isError ? (
        <p className="small" role="alert">
          The backend is not responding.
        </p>
      ) : null}
      {data ? (
        <>
          <div className="row" style={{ marginBottom: 10 }}>
            <Status ok={data.database} label="Database" />
            <Status ok={data.redis} label="Redis" />
            <span className="badge badge--neutral">Trading: none (view only)</span>
          </div>
          <dl className="kv">
            <dt>Uptime</dt>
            <dd className="num">{Math.round(data.uptimeSeconds / 60)} min</dd>
            <dt>Data source</dt>
            <dd>{data.dataSource}</dd>
            <dt>WebSocket clients</dt>
            <dd className="num">
              {Object.entries(data.websocketClients)
                .map(([k, v]) => `${k} ${v}`)
                .join(' · ')}
            </dd>
            <dt>Your streams</dt>
            <dd className="small">
              {Object.entries(connections)
                .map(([k, v]) => `${k}: ${v}`)
                .join(' · ')}
            </dd>
            <dt>Missed-event resyncs</dt>
            <dd className="num">{gaps}</dd>
            <dt>Quotes accepted / rejected</dt>
            <dd className="num">
              {counters['market.quotes_accepted'] ?? 0} / {counters['market.quotes_rejected'] ?? 0}
            </dd>
            <dt>Reconnects</dt>
            <dd className="num">{counters['market.reconnects'] ?? 0}</dd>
            <dt>Stale episodes</dt>
            <dd className="num">{counters['market.stale_transitions'] ?? 0}</dd>
            <dt>Dropped WS messages</dt>
            <dd className="num">{counters['ws.dropped_messages'] ?? 0}</dd>
            <dt>Queue depth</dt>
            <dd className="num">{data.metrics.gauges['persistence.queue_depth'] ?? 0}</dd>
            <dt>Errors</dt>
            <dd className="num">{data.metrics.error_count}</dd>
          </dl>
        </>
      ) : null}
    </Panel>
  );
}

export function AuditPanel({ limit = 12 }: { limit?: number }) {
  const { data, isError, isLoading } = useAuditEvents(limit);
  const events = data?.events ?? [];
  return (
    <Panel title="Audit trail" meta="append-only">
      {isLoading ? <p className="small muted">Loading audit trail…</p> : null}
      {isError ? <p className="small muted">Audit trail unavailable.</p> : null}
      {data && !events.length ? <Empty title="No audit events yet" /> : null}
      {events.length ? (
        <ul className="list">
          {events.map((e) => (
            <li key={e.id}>
              <div className="row small" style={{ justifyContent: 'space-between' }}>
                <strong>{humanize(e.action)}</strong>
                <span className="muted num">{dateTime(e.occurredAt)}</span>
              </div>
              <div className="small muted">
                {e.actor} · {e.entityType}
                {e.entityId ? ` ${e.entityId.slice(0, 8)}` : ''}
                {e.correlationId ? ` · corr ${e.correlationId.slice(0, 8)}` : ''}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </Panel>
  );
}
